import type { MapDef } from '../data/maps/foundry.js';
import type { Vec2 } from '../utils/vec2.js';

export interface PathGrid {
  cellsX: number;
  cellsY: number;
  tilePx: number;
  width: number;
  height: number;
  // Row-major, length = cellsX * cellsY. true = blocked. Backed by Uint8Array so the
  // indexed access never returns undefined under noUncheckedIndexedAccess.
  blocked: Uint8Array;
}

export function buildGrid(map: MapDef): PathGrid {
  const cellsX = Math.ceil(map.width / map.tilePx);
  const cellsY = Math.ceil(map.height / map.tilePx);
  const blocked = new Uint8Array(cellsX * cellsY);
  // Mark cells that extend past world bounds. Squads can't legally enter them and
  // pathfinding must treat them as walls so A* doesn't route through phantom cells.
  for (let cy = 0; cy < cellsY; cy++) {
    for (let cx = 0; cx < cellsX; cx++) {
      const x1 = cx * map.tilePx + map.tilePx;
      const y1 = cy * map.tilePx + map.tilePx;
      if (x1 > map.width || y1 > map.height) {
        blocked[cy * cellsX + cx] = 1;
      }
    }
  }
  for (const o of map.obstacles) {
    const x0 = Math.floor(o.x / map.tilePx);
    const y0 = Math.floor(o.y / map.tilePx);
    const x1 = Math.ceil((o.x + o.w) / map.tilePx);
    const y1 = Math.ceil((o.y + o.h) / map.tilePx);
    for (let cy = y0; cy < y1; cy++) {
      for (let cx = x0; cx < x1; cx++) {
        if (cx < 0 || cy < 0 || cx >= cellsX || cy >= cellsY) continue;
        blocked[cy * cellsX + cx] = 1;
      }
    }
  }
  return { cellsX, cellsY, tilePx: map.tilePx, width: map.width, height: map.height, blocked };
}

export function worldToCell(grid: PathGrid, worldX: number, worldY: number): { cx: number; cy: number } {
  const cx = clamp(Math.floor(worldX / grid.tilePx), 0, grid.cellsX - 1);
  const cy = clamp(Math.floor(worldY / grid.tilePx), 0, grid.cellsY - 1);
  return { cx, cy };
}

export function cellCenter(grid: PathGrid, cx: number, cy: number): Vec2 {
  return { x: cx * grid.tilePx + grid.tilePx / 2, y: cy * grid.tilePx + grid.tilePx / 2 };
}

export function isBlocked(grid: PathGrid, cx: number, cy: number): boolean {
  if (cx < 0 || cy < 0 || cx >= grid.cellsX || cy >= grid.cellsY) return true;
  return grid.blocked[cy * grid.cellsX + cx] === 1;
}

function clamp(v: number, lo: number, hi: number): number {
  return v < lo ? lo : v > hi ? hi : v;
}
